import React, { useEffect, useState } from 'react';
import EmailVerificationPanel from './EmailVerificationPanel';
import { VERIFICATION_PURPOSE, isValidEmail } from '../../utils/verification';

const COPY = {
  en: {
    title: 'Email',
    verified: 'Verified',
    unverified: 'Not verified',
    noEmail: 'No email bound',
    bind: 'Bind email',
    change: 'Change email',
    cancel: 'Cancel',
    emailLabel: 'Email address',
    emailPlaceholder: 'your@email.com',
    emailError: 'Please enter a valid email address.',
    sameEmail: 'This is already your verified email.',
    verifySuccess: 'Email updated and verified.',
  },
  zh: {
    title: '邮箱',
    verified: '已验证',
    unverified: '未验证',
    noEmail: '尚未绑定邮箱',
    bind: '绑定邮箱',
    change: '更换邮箱',
    cancel: '取消',
    emailLabel: '邮箱地址',
    emailPlaceholder: 'your@email.com',
    emailError: '请输入有效的邮箱地址。',
    sameEmail: '该邮箱与当前已验证邮箱相同，无需变更。',
    verifySuccess: '邮箱已更新并完成验证。',
  },
};

function EmailBindSection({ email, emailVerified, lang, onVerified, embedded = false }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!editing) {
      setNewEmail('');
      setEmailError('');
      setVerifyEmail('');
      setSuccessMessage('');
    }
  }, [editing]);

  const startBind = () => {
    setEditing(true);
    setNewEmail(email || '');
  };

  const proceedToVerify = () => {
    setEmailError('');
    setSuccessMessage('');
    const trimmed = newEmail.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError(t.emailError);
      return;
    }
    if (emailVerified && trimmed.toLowerCase() === String(email || '').trim().toLowerCase()) {
      setEmailError(t.sameEmail);
      return;
    }
    setVerifyEmail(trimmed);
  };

  const handleVerified = async () => {
    setSuccessMessage(t.verifySuccess);
    setEditing(false);
    setVerifyEmail('');
    if (typeof onVerified === 'function') {
      await onVerified();
    }
  };

  return (
    <div className={`email-bind-section${embedded ? ' email-bind-embedded' : ' profile-card'}`}>
      {!embedded ? (
        <div className="email-bind-header">
          <h2 className="profile-section-title">{t.title}</h2>
          {!editing && (
            <button type="button" className="btn-secondary btn-sm" onClick={startBind}>
              {email ? t.change : t.bind}
            </button>
          )}
        </div>
      ) : null}

      {!editing ? (
        <div className="email-bind-display">
          {!embedded ? (
            <div className="profile-meta-item">
              <span className="profile-meta-label">{t.emailLabel}</span>
              <span className="profile-meta-value">{email || t.noEmail}</span>
            </div>
          ) : null}
          {!embedded && email ? (
            <span
              className={`email-status-badge${emailVerified ? ' is-verified' : ' is-unverified'}`}
            >
              {emailVerified ? t.verified : t.unverified}
            </span>
          ) : null}
          {embedded ? (
            <div className="form-actions email-bind-actions">
              <button type="button" onClick={startBind}>
                {email ? t.change : t.bind}
              </button>
            </div>
          ) : null}
          {successMessage ? <p className="profile-success">{successMessage}</p> : null}
        </div>
      ) : !verifyEmail ? (
        <div className="email-bind-form">
          <label className="profile-field">
            <span className="profile-field-label">{t.emailLabel}</span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder={t.emailPlaceholder}
            />
          </label>
          {emailError ? <p className="form-error">{emailError}</p> : null}
          <div className="form-actions email-bind-actions">
            <button type="button" onClick={proceedToVerify}>
              {t.bind}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
              {t.cancel}
            </button>
          </div>
        </div>
      ) : (
        <EmailVerificationPanel
          email={verifyEmail}
          purpose={VERIFICATION_PURPOSE.BIND_EMAIL}
          lang={lang}
          onSuccess={handleVerified}
        />
      )}
    </div>
  );
}

export default EmailBindSection;
